import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { PostCard } from '../../timeline/components/PostCard';
import { useAuth } from '../../../hooks/useAuth';

interface ArtistEchoesProps {
  userId: string;
  accentColor: string;
  compact?: boolean;
}

interface Post {
  id: string;
  artist_id: string;
  content: string;
  media_url: string | null;
  required_fan_level: number;
  created_at: string;
  is_hidden: boolean;
  profiles: {
    display_name: string;
    avatar_url: string | null;
    user_type: string;
  };
}

const PAGE_SIZE = 5;

type FilterLevel = 'ALL' | 1 | 2 | 3;

const FILTER_TABS: { label: string; value: FilterLevel; icon: string }[] = [
  { label: 'ALL', value: 'ALL', icon: 'ri-stack-line' },
  { label: 'LV.1+', value: 1, icon: 'ri-seedling-line' },
  { label: 'LV.2+', value: 2, icon: 'ri-fire-line' },
  { label: 'LV.3+', value: 3, icon: 'ri-vip-crown-line' },
];

export function ArtistEchoes({ userId, accentColor, compact = false }: ArtistEchoesProps) {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userFanLevel, setUserFanLevel] = useState(0);
  const [supportTotals, setSupportTotals] = useState<Record<string, number>>({});
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeFilter, setActiveFilter] = useState<FilterLevel>('ALL');

  useEffect(() => {
    const fetchFanLevel = async () => {
      if (!user || !userId) return;
      try {
        const { data, error } = await supabase.rpc('get_user_artist_depth_details', {
          p_user_id: user.id,
          p_artist_id: userId,
        });
        if (error) throw error;
        setUserFanLevel(data?.fanLevel ?? 0);
      } catch (err) {
        console.error('Failed to fetch fan level:', err);
      }
    };
    fetchFanLevel();
  }, [user, userId]);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('artist_posts')
          .select(`
            id,
            artist_id,
            content,
            media_url,
            required_fan_level,
            created_at,
            is_hidden
          `)
          .eq('artist_id', userId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // profilesを別途取得
        let postsWithProfiles: Post[] = (data || []).map((p) => ({
          ...p,
          profiles: { display_name: 'Unknown', avatar_url: null, user_type: 'maker' },
        }));

        if (data && data.length > 0) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('id, display_name, avatar_url, user_type')
            .eq('id', userId)
            .maybeSingle();

          if (profileData) {
            postsWithProfiles = postsWithProfiles.map((p) => ({
              ...p,
              profiles: {
                display_name: profileData.display_name,
                avatar_url: profileData.avatar_url,
                user_type: profileData.user_type,
              },
            }));
          }

          const postIds = data.map((p) => p.id);
          const { data: payments, error: paymentError } = await supabase
            .from('resonance_payments')
            .select('post_id, amount')
            .in('post_id', postIds)
            .eq('status', 'succeeded');

          if (paymentError) throw paymentError;

          const totals: Record<string, number> = {};
          (payments || []).forEach((payment: { post_id: string; amount: number }) => {
            totals[payment.post_id] = (totals[payment.post_id] || 0) + payment.amount;
          });
          setSupportTotals(totals);
        }

        setPosts(postsWithProfiles);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchPosts();
    }
  }, [userId]);

  // フィルター変更時にページをリセット
  const handleFilterChange = (filter: FilterLevel) => {
    setActiveFilter(filter);
    setVisibleCount(PAGE_SIZE);
  };

  // フィルタリングされた投稿
  const filteredPosts = useMemo(() => {
    if (activeFilter === 'ALL') return posts;
    return posts.filter((p) => p.required_fan_level >= activeFilter);
  }, [posts, activeFilter]);

  // 各レベルの投稿数
  const levelCounts = useMemo(() => ({
    ALL: posts.length,
    1: posts.filter((p) => p.required_fan_level >= 1).length,
    2: posts.filter((p) => p.required_fan_level >= 2).length,
    3: posts.filter((p) => p.required_fan_level >= 3).length,
  }), [posts]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await new Promise((resolve) => setTimeout(resolve, 300));
    setVisibleCount((prev) => prev + PAGE_SIZE);
    setLoadingMore(false);
  };

  if (loading) {
    return (
      <div className={`${compact ? 'py-12' : 'py-16'}`}>
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: accentColor }} />
          <span className="text-gray-500 text-sm tracking-wider">LOADING ECHOES...</span>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className={`${compact ? 'py-12' : 'py-16'}`}>
        <div className="flex items-center gap-3 mb-8">
          <div
            className="w-2 h-2 rounded-full"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-wider"
            style={{ color: accentColor }}
          >
            ECHOES
          </h2>
        </div>
        <div
          className="rounded-2xl p-12 text-center"
          style={{
            background: 'rgba(10, 10, 10, 0.5)',
            border: `1px solid ${accentColor}20`,
          }}
        >
          <i className="ri-chat-3-line text-5xl mb-4" style={{ color: `${accentColor}40` }} />
          <p className="text-gray-500 text-sm tracking-wide">まだ投稿がありません</p>
        </div>
      </div>
    );
  }

  const authorType = posts[0]?.profiles?.user_type === 'node' ? 'node' : 'maker';
  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPosts.length;
  const remaining = filteredPosts.length - visibleCount;

  return (
    <div className={`${compact ? 'py-12' : 'py-16'}`}>
      {/* ヘッダー */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
        />
        <h2
          className="font-display text-2xl sm:text-3xl font-bold tracking-wider"
          style={{ color: accentColor }}
        >
          ECHOES
        </h2>
        <span className="text-gray-500 text-sm tracking-wider ml-2">
          {posts.length} {posts.length === 1 ? 'POST' : 'POSTS'}
        </span>
      </div>

      {/* フィルタータブ */}
      <div className="mb-8">
        <div
          className="inline-flex items-center gap-1 p-1 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {FILTER_TABS.map((tab) => {
            const isActive = activeFilter === tab.value;
            const count = levelCounts[tab.value as keyof typeof levelCounts];
            return (
              <motion.button
                key={String(tab.value)}
                onClick={() => handleFilterChange(tab.value)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={
                  isActive
                    ? {
                        background: `linear-gradient(135deg, ${accentColor}22, ${accentColor}10)`,
                        border: `1px solid ${accentColor}55`,
                        color: accentColor,
                        boxShadow: `0 0 16px ${accentColor}22`,
                      }
                    : {
                        background: 'transparent',
                        border: '1px solid transparent',
                        color: 'rgba(156,163,175,0.8)',
                      }
                }
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`} />
                </span>
                <span>{tab.label}</span>
                {/* 件数バッジ */}
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-black leading-none"
                  style={
                    isActive
                      ? { background: `${accentColor}33`, color: accentColor }
                      : { background: 'rgba(255,255,255,0.06)', color: 'rgba(156,163,175,0.6)' }
                  }
                >
                  {count}
                </span>
                {/* アクティブインジケーター */}
                {isActive && (
                  <motion.div
                    layoutId="echo-filter-indicator"
                    className="absolute inset-0 rounded-lg pointer-events-none"
                    style={{ boxShadow: `inset 0 0 12px ${accentColor}18` }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* フィルター説明テキスト */}
        <AnimatePresence mode="wait">
          {activeFilter !== 'ALL' && (
            <motion.p
              key={String(activeFilter)}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="mt-3 text-xs tracking-wider"
              style={{ color: `${accentColor}88` }}
            >
              <i className="ri-lock-line mr-1" />
              ファンレベル {activeFilter} 以上が必要な投稿を表示中
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* 投稿なし（フィルター後） */}
      <AnimatePresence mode="wait">
        {filteredPosts.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl p-12 text-center"
            style={{
              background: 'rgba(10, 10, 10, 0.5)',
              border: `1px solid ${accentColor}20`,
            }}
          >
            <i className="ri-filter-off-line text-4xl mb-3" style={{ color: `${accentColor}40` }} />
            <p className="text-gray-500 text-sm tracking-wide">
              このレベルの投稿はありません
            </p>
          </motion.div>
        ) : (
          <motion.div
            key={`list-${activeFilter}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="space-y-6"
          >
            <AnimatePresence initial={false}>
              {visiblePosts.map((post, index) => {
                const isLocked = post.required_fan_level > userFanLevel;
                const postDate = new Date(post.created_at).toLocaleDateString('ja-JP', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });

                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4, delay: index < PAGE_SIZE ? index * 0.08 : 0 }}
                  >
                    {/* ファンレベルバッジ */}
                    {post.required_fan_level > 0 && (
                      <div className="flex items-center gap-2 mb-2 ml-1">
                        <span
                          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-widest"
                          style={{
                            background: `${accentColor}18`,
                            border: `1px solid ${accentColor}44`,
                            color: accentColor,
                          }}
                        >
                          <i className="ri-vip-crown-line text-xs" />
                          LV.{post.required_fan_level}+
                        </span>
                      </div>
                    )}
                    <PostCard
                      id={post.id}
                      artistId={post.artist_id}
                      artistName={post.profiles?.display_name || 'Unknown'}
                      artistAvatar={
                        post.profiles?.avatar_url ||
                        'https://public.readdy.ai/ai/img_res/5e7127a583fce2a9414a913d92bc34c2.jpg'
                      }
                      postDate={postDate}
                      imageUrl={
                        post.media_url ||
                        'https://readdy.ai/api/search-image?query=underground%20music%20abstract%20neon%20dark%20background&width=800&height=450&seq=post-default&orientation=landscape'
                      }
                      content={post.content}
                      requiredFanLevel={post.required_fan_level}
                      supportTotal={supportTotals[post.id] || 0}
                      isLocked={isLocked}
                      authorType={authorType}
                      is_hidden={post.is_hidden}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* もっと見るボタン */}
      {hasMore && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8 flex flex-col items-center gap-3"
        >
          <div className="w-full max-w-xs">
            <div className="flex justify-between text-xs text-gray-600 mb-1.5 tracking-wider">
              <span>{visibleCount} / {filteredPosts.length} POSTS</span>
              <span>{Math.round((visibleCount / filteredPosts.length) * 100)}%</span>
            </div>
            <div
              className="w-full h-1 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)` }}
                initial={{ width: 0 }}
                animate={{ width: `${(visibleCount / filteredPosts.length) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="flex items-center gap-2.5 px-8 py-3 rounded-full font-bold text-sm tracking-widest cursor-pointer transition-all duration-300 disabled:opacity-60"
            style={{
              background: `rgba(${accentColor.replace('#', '').match(/.{2}/g)?.map(h => parseInt(h, 16)).join(',') || '0,217,255'}, 0.08)`,
              border: `1px solid ${accentColor}44`,
              color: accentColor,
              boxShadow: loadingMore ? 'none' : `0 0 20px ${accentColor}22`,
            }}
          >
            {loadingMore ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                <span>読み込み中...</span>
              </>
            ) : (
              <>
                <i className="ri-arrow-down-line text-base" />
                <span>もっと見る</span>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-black"
                  style={{ background: `${accentColor}22`, color: accentColor }}
                >
                  +{Math.min(remaining, PAGE_SIZE)}
                </span>
              </>
            )}
          </motion.button>
        </motion.div>
      )}

      {/* 全件表示済みメッセージ */}
      {!hasMore && filteredPosts.length > PAGE_SIZE && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mt-8 flex flex-col items-center gap-2"
        >
          <div
            className="w-full h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${accentColor}44, transparent)` }}
          />
          <p className="text-xs text-gray-600 tracking-widest pt-2">
            ALL {filteredPosts.length} ECHOES LOADED
          </p>
        </motion.div>
      )}
    </div>
  );
}
